import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

export default function RealPropertyModal({ isOpen, onClose, caseId, item, onSave, exemptions = [] }) {
  const [form, setForm] = useState({
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip_code: '',
    county: '',
    monthly_payment: '',
    second_monthly_payment: '',
    principal_residence: false,
    property_type: 'single_family',
    ownership: 'debtor1',
    ownership_type: 'fee_simple',
    community_property: false,
    other_info: '',
    value_total: '',
    value_ownership_portion: '',
    exemption_code: '',
    exemption_amount: '',
  });

  useEffect(() => {
    if (item) {
      setForm({
        street1: item.street1 || '',
        street2: item.street2 || '',
        city: item.city || '',
        state: item.state || '',
        zip_code: item.zip_code || '',
        county: item.county || '',
        monthly_payment: item.monthly_payment || '',
        second_monthly_payment: item.second_monthly_payment || '',
        principal_residence: item.principal_residence,
        property_type: item.property_type,
        ownership: item.ownership,
        ownership_type: item.ownership_type,
        community_property: item.community_property,
        other_info: item.other_info || '',
        value_total: item.value_total || '',
        value_ownership_portion: item.value_ownership_portion || '',
        exemption_code: item.exemption_code || '',
        exemption_amount: item.exemption_amount || '',
      });
    } else {
      // Reset form when adding new
      setForm({
        street1: '',
        street2: '',
        city: '',
        state: '',
        zip_code: '',
        county: '',
        monthly_payment: '',
        second_monthly_payment: '',
        principal_residence: false,
        property_type: 'single_family',
        ownership: 'debtor1',
        ownership_type: 'fee_simple',
        community_property: false,
        other_info: '',
        value_total: '',
        value_ownership_portion: '',
        exemption_code: '',
        exemption_amount: '',
      });
    }
  }, [item]);

  if (!isOpen) return null;

  const API_URL = process.env.REACT_APP_API_URL;
  const TOKEN = localStorage.getItem('accessToken');

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };


  const handleSubmit = async e => {
    e.preventDefault();
    const url = item
      ? `${API_URL}/case/${caseId}/real-properties/${item.id}/`
      : `${API_URL}/case/${caseId}/real-properties/`;
    const method = item ? 'PUT' : 'POST';
    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${TOKEN}`,
        },
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error('Save failed');
      const saved = await response.json();
      onSave(saved);
      onClose();
    } catch (err) {
      console.error(err);
      alert('Error saving property.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <form
        onSubmit={handleSubmit}
        className="bg-white p-6 rounded w-full max-w-4xl space-y-4 overflow-auto max-h-screen shadow-lg rounded-lg"
      >
        <h2 className="text-xl font-semibold">{item ? 'Edit' : 'Add'} Real Property</h2>

        {/* Address Fields */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            name="street1"
            value={form.street1}
            onChange={handleChange}
            placeholder="Street 1"
            required
            className="border p-2 rounded col-span-1 md:col-span-2"
          />
          <input
            name="street2"
            value={form.street2}
            onChange={handleChange}
            placeholder="Street 2"
            className="border p-2 rounded col-span-1 md:col-span-2"
          />
          <input
            name="city"
            value={form.city}
            onChange={handleChange}
            placeholder="City"
            className="border p-2 rounded col-span-1 md:col-span-2"
          />
          <input
            name="state"
            value={form.state}
            onChange={handleChange}
            placeholder="State"
            className="border p-2 rounded col-span-1 md:col-span-1 w-full"
          />
          <input
            name="zip_code"
            value={form.zip_code}
            onChange={handleChange}
            placeholder="ZIP Code"
            className="border p-2 rounded col-span-1 md:col-span-1"
          />
          <input
            name="county"
            value={form.county}
            onChange={handleChange}
            placeholder="County"
            className="border p-2 rounded col-span-1 md:col-span-4"
          />
        </div>

        {/* Payment Fields */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            name="monthly_payment"
            type="number"
            step="0.01"
            value={form.monthly_payment}
            onChange={handleChange}
            placeholder="Monthly Payment"
            className="border p-2 rounded col-span-1 md:col-span-2"
          />
          <input
            name="second_monthly_payment"
            type="number"
            step="0.01"
            value={form.second_monthly_payment}
            onChange={handleChange}
            placeholder="2nd Mortgage Payment"
            className="border p-2 rounded col-span-1 md:col-span-2"
          />
        </div>

        {/* Checkbox Fields */}
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="principal_residence"
              checked={form.principal_residence}
              onChange={handleChange}
              className="mr-2"
            />
            Principal Residence
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="community_property"
              checked={form.community_property}
              onChange={handleChange}
              className="mr-2"
            />
            Community Property
          </label>
        </div>

        {/* Select Fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select
            name="property_type"
            value={form.property_type}
            onChange={handleChange}
            className="border p-2 rounded"
          >
            <option value="single_family">Single-Family Home</option>
            <option value="duplex">Duplex</option>
            <option value="condo">Condominium</option>
            <option value="manufactured">Manufactured Home</option>
            <option value="land">Land</option>
            <option value="investment">Investment Property</option>
            <option value="timeshare">Timeshare</option>
            <option value="other">Other</option>
          </select>
          <select
            name="ownership"
            value={form.ownership}
            onChange={handleChange}
            className="border p-2 rounded"
          >
            <option value="debtor1">Debtor 1</option>
            <option value="debtor2">Debtor 2</option>
            <option value="d1_d2">Debtor 1 & Debtor 2</option>
            <option value="joint_third">Joint with Third Party</option>
          </select>
          <select
            name="ownership_type"
            value={form.ownership_type}
            onChange={handleChange}
            className="border p-2 rounded"
          >
            <option value="fee_simple">Fee Simple</option>
            <option value="tenancy_entireties">Tenancy by the Entireties</option>
            <option value="life_estate">Life Estate</option>
            <option value="remainder">Remainder</option>
            <option value="joint">Joint</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Value Fields */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            name="value_total"
            type="number"
            step="0.01"
            value={form.value_total}
            onChange={handleChange}
            placeholder="Total Value"
            className="border p-2 rounded col-span-1 md:col-span-2"
          />
          <input
            name="value_ownership_portion"
            type="number"
            step="0.01"
            value={form.value_ownership_portion}
            onChange={handleChange}
            placeholder="Ownership Portion Value"
            className="border p-2 rounded col-span-1 md:col-span-2"
          />
        </div>

        {/* Other Info */}
        <textarea
          name="other_info"
          value={form.other_info}
          onChange={handleChange}
          placeholder="Other Info"
          className="w-full border p-2 rounded"
        />

        {/* Exemption Fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block">
            <span className="text-sm font-medium">Exemption</span>
            <select
              name="exemption_code"
              value={form.exemption_code}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded max-h-48 overflow-auto text-sm"
            >
              <option value="">None</option>
              {exemptions.map(ex => (
                <option
                  key={ex.key}
                  value={ex.law}
                  title={ex.asset_description}
                >
                  {`${ex.asset_name} ($${parseFloat(ex.limit).toFixed(2)}) – ${ex.asset_description}`}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium">Exemption Amount</span>
            <input
              name="exemption_amount"
              type="number"
              step="0.01"
              value={form.exemption_amount}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
            />
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

RealPropertyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  caseId: PropTypes.string.isRequired,
  item: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  exemptions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    asset_name: PropTypes.string.isRequired,
    limit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  })).isRequired,
};